import { ethers } from 'ethers';
import logger from './logger';

/**
 * Utility to make contract calls with proper block handling to avoid BlockOutOfRangeError
 */
export class ContractCallHelper {
    /**
     * Create a fresh provider instance to avoid cached block issues
     */
    static createFreshProvider(existingProvider: ethers.providers.Provider): ethers.providers.Provider {
        // If it's a Web3Provider, create a fresh instance
        if (existingProvider instanceof ethers.providers.Web3Provider && window.ethereum) {
            return new ethers.providers.Web3Provider(
                window.ethereum as any,
                "any" // Allow any network to avoid network mismatch issues
            );
        }

        // For other provider types, return as-is
        return existingProvider;
    }

    /**
     * Make a contract call with proper error handling and block management
     */
    static async makeContractCall<T>(
        contractAddress: string,
        abi: string[] | ethers.utils.Interface,
        functionName: string,
        params: any[] = [],
        provider: ethers.providers.Provider,
        options: {
            blockTag?: string | number;
            retryOnBlockError?: boolean;
            maxRetries?: number;
        } = {}
    ): Promise<T> {
        const {
            blockTag = "latest",
            retryOnBlockError = true,
            maxRetries = 2
        } = options;

        let lastError: Error | null = null;

        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                // Use fresh provider on retry attempts
                const providerToUse = attempt > 0
                    ? this.createFreshProvider(provider)
                    : provider;

                const contract = new ethers.Contract(
                    contractAddress,
                    abi,
                    providerToUse
                );

                // Make the call with explicit block tag
                const result = await contract[functionName](...params, {
                    blockTag: blockTag
                });

                logger.log(`Contract call successful: ${functionName}`, {
                    contractAddress,
                    attempt: attempt + 1,
                    blockTag
                });

                return result;
            } catch (error) {
                lastError = error as Error;
                const errorMessage = error instanceof Error ? error.message : String(error);

                logger.warn(`Contract call attempt ${attempt + 1} failed:`, {
                    functionName,
                    contractAddress,
                    error: errorMessage
                });

                // Check if this is a block-related error that we should retry
                const isBlockError = errorMessage.includes("BlockOutOfRangeError") ||
                    errorMessage.includes("block height") ||
                    errorMessage.includes("call exception") ||
                    errorMessage.includes("Internal JSON-RPC error") ||
                    errorMessage.includes("missing revert data");

                if (!retryOnBlockError || !isBlockError || attempt >= maxRetries) {
                    break;
                }

                // Wait a bit before retrying
                await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
            }
        }

        // If we get here, all attempts failed
        const errorMessage = lastError?.message || 'Unknown error';
        let enhancedError = errorMessage;

        if (errorMessage.includes("BlockOutOfRangeError") || errorMessage.includes("block height")) {
            enhancedError = "Block height mismatch detected. This usually happens when the network state is out of sync. Try refreshing the page or reconnecting your wallet.";
        } else if (errorMessage.includes("Internal JSON-RPC error")) {
            enhancedError = "MetaMask RPC error detected. This usually happens when the network is congested or your wallet connection is unstable. Try refreshing the page or reconnecting your wallet.";
        } else if (errorMessage.includes("missing revert data")) {
            enhancedError = "Contract call failed with missing revert data. This might indicate the contract function doesn't exist or the contract address is incorrect.";
        } else if (errorMessage.includes("call exception")) {
            enhancedError = "Contract call failed. Please verify the contract address and ensure you're connected to the correct network.";
        }

        throw new Error(`Contract call failed after ${maxRetries + 1} attempts: ${enhancedError}`);
    }

    /**
     * Convenience method for common view function calls
     */
    static async callViewFunction<T>(
        contractAddress: string,
        functionSignature: string,
        params: any[] = [],
        provider: ethers.providers.Provider
    ): Promise<T> {
        const abi = [functionSignature];
        const functionName = functionSignature.split('(')[0].replace('function ', '');

        return this.makeContractCall<T>(
            contractAddress,
            abi,
            functionName,
            params,
            provider,
            { blockTag: "latest", retryOnBlockError: true }
        );
    }

    /**
     * Specific helper for getMinDelay calls
     */
    static async getMinDelay(
        contractAddress: string,
        provider: ethers.providers.Provider
    ): Promise<string> {
        try {
            const result = await this.callViewFunction<ethers.BigNumber>(
                contractAddress,
                "function getMinDelay() view returns (uint256)",
                [],
                provider
            );

            return result.toString();
        } catch (error) {
            logger.error("Error in getMinDelay helper:", error);
            throw error;
        }
    }

    /**
     * Specific helper for getOwners calls
     */
    static async getOwners(
        contractAddress: string,
        provider: ethers.providers.Provider
    ): Promise<string[]> {
        try {
            return await this.callViewFunction<string[]>(
                contractAddress,
                "function getOwners() view returns (address[])",
                [],
                provider
            );
        } catch (error) {
            logger.error("Error in getOwners helper:", error);
            throw error;
        }
    }

    /**
     * Specific helper for getThreshold calls
     */
    static async getThreshold(
        contractAddress: string,
        provider: ethers.providers.Provider
    ): Promise<number> {
        try {
            const result = await this.callViewFunction<ethers.BigNumber>(
                contractAddress,
                "function getThreshold() view returns (uint256)",
                [],
                provider
            );

            return result.toNumber();
        } catch (error) {
            logger.error("Error in getThreshold helper:", error);
            throw error;
        }
    }
}

export default ContractCallHelper;
